/**
 * PATH: packages/ui/src/kanban/components/board-table/BoardTableRowTr.tsx
 * FILE_ID: UI-KANBAN-TABLE-ROW-001
 * PURPOSE: One Table-view card row (<tr>) — click/keyboard opens the card,
 *          right-click opens the host card menu (P2 right-click parity).
 * WHY: Verbatim extraction from BoardTableView's tbody map so the flow and
 *      virtualized branches render the identical row; memoized so a 14k-row
 *      board doesn't re-render every row on sort-state changes.
 */
import { memo } from 'react'
import type { Card } from '../../types'
import { useCardSurfaceCommands } from '../../card-surface/projections/useCardSurfaceCommands'
import { PRIORITY_CLS, PRIORITY_LABEL } from './constants'
import type { BoardTableRow } from './useBoardTableRows'

export interface BoardTableRowTrProps {
  row: BoardTableRow
  onCardOpen?: (card: Card) => void
  onCardContextMenu?: (card: Card, anchor: { x: number; y: number }) => void
  /** Virtualized branch only — virtualizer.measureElement + the item index
   *  it reads off `data-index`. Undefined in flow render. */
  measureRef?: (el: Element | null) => void
  virtualIndex?: number
}

export const BoardTableRowTr = memo(function BoardTableRowTr({
  row,
  onCardOpen,
  onCardContextMenu,
  measureRef,
  virtualIndex,
}: BoardTableRowTrProps) {
  const c = row.card
  const commands = useCardSurfaceCommands('table-row')
  return (
    <tr
      key={c.id}
      ref={measureRef}
      data-card-id={c.id}
      data-card-surface="table-row"
      data-index={virtualIndex}
      tabIndex={0}
      role="button"
      aria-label={`Open card ${c.title}`}
      onClick={() => commands.open(c, onCardOpen)}
      onContextMenu={
        onCardContextMenu
          ? (e) => {
              e.preventDefault()
              commands.openContextMenu(
                c,
                { x: e.clientX, y: e.clientY },
                onCardContextMenu,
              )
            }
          : undefined
      }
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          commands.open(c, onCardOpen)
        }
      }}
      className="cursor-pointer border-b border-neutral-100 dark:border-neutral-900 hover:bg-blue-50/40 dark:hover:bg-blue-950/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:bg-blue-50/40 dark:focus-visible:bg-blue-950/20"
    >
      <td className="px-3 py-2">
        <div className="text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate max-w-md">
          {c.title}
        </div>
        {c.summary && (
          <div className="text-[11px] text-neutral-500 truncate max-w-md">
            {c.summary}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-neutral-600 dark:text-neutral-300">
        {row.laneName ?? '—'}
      </td>
      <td className={['px-3 py-2 text-xs', row.overdue ? 'text-red-600 font-semibold' : 'text-neutral-600 dark:text-neutral-300'].join(' ')}>
        {row.due ? row.due.toLocaleDateString() : '—'}
      </td>
      <td className={['px-3 py-2 text-xs font-medium', PRIORITY_CLS[c.priority ?? 1]].join(' ')}>
        {PRIORITY_LABEL[c.priority ?? 1]}
      </td>
      <td className="px-3 py-2 text-xs text-neutral-500">
        {new Date(c.updated_at).toLocaleDateString()}
      </td>
    </tr>
  )
})
